import { useEffect, useState } from "react";
import { Scene } from "./components/Scene";
import { story } from "./stories";
import { ArrowLeft, BrandMark, CornerMark, RestartIcon } from "./components/icons";
import "./App.css";

export function App() {
  const [sceneId, setSceneId] = useState<string>(story.start);
  const [history, setHistory] = useState<string[]>([]);
  const [clock, setClock] = useState<string>(formatClock(new Date()));

  useEffect(() => {
    const tick = setInterval(() => setClock(formatClock(new Date())), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [sceneId]);

  // The validator + CI guarantee every sceneId reachable in the graph exists.
  // If this throws, the story file shipped without validation — fail loud.
  const scene = story.scenes[sceneId];
  if (!scene) {
    throw new Error(`Unknown scene id: ${sceneId}. validate-stories should have caught this.`);
  }
  const step = history.length + 1;

  const goTo = (target: string) => {
    setHistory((h) => [...h, sceneId]);
    setSceneId(target);
  };

  const goBack = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setSceneId(prev);
  };

  const restart = () => {
    setHistory([]);
    setSceneId(story.start);
  };

  return (
    <div className="stage">
      <div className="reg tl"><CornerMark /></div>
      <div className="reg tr"><CornerMark /></div>
      <div className="reg bl"><CornerMark /></div>
      <div className="reg br"><CornerMark /></div>

      <header className="topstrip">
        <div className="brand">
          <span className="mark"><BrandMark /></span>
          <span>
            ROUTE<span className="dot">·</span>2<span className="dot">·</span>UDDI
          </span>
        </div>
        <div className="meta">
          <span>NODE <b>{sceneId}</b></span>
          <span>STEP <b>{String(step).padStart(2, "0")}</b></span>
        </div>
        <div className="spacer" />
        <div className="clock">{clock}</div>
      </header>

      <main className="engine-wrap">
        <nav className="crumb-bar" aria-label="Path">
          <button
            className="back-btn"
            onClick={goBack}
            disabled={history.length === 0}
            aria-label="Back to previous scene"
          >
            <ArrowLeft />
            Back
          </button>
          <div className="crumbs">
            {history.length > 0 && (
              <>
                {history.slice(-4).map((id, i, arr) => (
                  <span key={`${i}:${id}`} className="c">
                    {id}
                    {i < arr.length - 1 ? <span className="sep"> / </span> : null}
                  </span>
                ))}
                <span className="sep"> / </span>
              </>
            )}
            <span className="c now">{sceneId}</span>
          </div>
          <button className="restart-btn" onClick={restart} title="Start over">
            <RestartIcon />
            Restart
          </button>
        </nav>

        <Scene scene={scene} step={step} onChoose={goTo} />
      </main>

      <footer className="ticker">
        <span>{story.title} · v0.1</span>
        <span>{Object.keys(story.scenes).length} SCENES · ENGINE READY</span>
        <span>VECTOR 001/A</span>
      </footer>
    </div>
  );
}

function formatClock(d: Date): string {
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss} UTC`;
}
